import React from "react";
import { Table, Tag, Divider, Empty } from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { RawMaterialPurchase, RawMaterialPurchaseItem } from "../../../Types";
import { PurchasePaymentStatus } from "../../../Types";

interface TotalRowProps {
  label: string;
  value: string;
  valueClass?: string;
  bold?: boolean;
}

interface InfoCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const PAYMENT_STATUS_COLOR: Record<PurchasePaymentStatus, string> = {
  [PurchasePaymentStatus.PAID]: "#16a34a",
  [PurchasePaymentStatus.PARTIAL]: "#d97706",
  [PurchasePaymentStatus.DUE]: "#dc2626",
};

const PAYMENT_TAG_COLOR: Record<PurchasePaymentStatus, string> = {
  [PurchasePaymentStatus.PAID]: "success",
  [PurchasePaymentStatus.PARTIAL]: "warning",
  [PurchasePaymentStatus.DUE]: "error",
};

interface Props {
  purchase: RawMaterialPurchase;
}

const itemColumns = [
  {
    title: "#",
    key: "index",
    width: 40,
    render: (_: any, __: any, idx: number) => (
      <span className="text-xs text-gray-400 font-medium">{idx + 1}</span>
    ),
  },
  {
    title: "Material",
    key: "material",
    render: (_: any, rec: RawMaterialPurchaseItem) => (
      <div>
        <div className="font-medium text-gray-800 text-sm">
          {rec.raw_material_name}
        </div>
        <div className="text-[11px] text-gray-400 font-mono">
          {rec.raw_material_code}
        </div>
      </div>
    ),
  },
  {
    title: "Batch",
    dataIndex: "batch_no",
    key: "batch_no",
    render: (v: string) => (
      <Tag className="text-[11px] font-mono">{v || "—"}</Tag>
    ),
  },
  {
    title: "Unit Cost",
    dataIndex: "cost_price",
    key: "cost_price",
    align: "right" as const,
    render: (v: number) => (
      <span className="text-xs text-gray-700">Rs. {v.toFixed(2)}</span>
    ),
  },
  {
    title: "Qty",
    key: "quantity",
    align: "right" as const,
    render: (_: any, rec: RawMaterialPurchaseItem) => (
      <span className="text-xs font-semibold text-gray-700">
        {rec.quantity}{" "}
        <span className="text-gray-400 font-normal capitalize">
          {rec.raw_material_unit}
        </span>
      </span>
    ),
  },
  {
    title: "Disc.",
    dataIndex: "discount_percent",
    key: "discount_percent",
    align: "right" as const,
    render: (v: number) => (
      <span className="text-xs text-gray-500">{v > 0 ? `${v}%` : "—"}</span>
    ),
  },
  {
    title: "Subtotal",
    dataIndex: "total",
    key: "total",
    align: "right" as const,
    render: (v: number) => (
      <span className="text-sm font-semibold text-gray-800">
        Rs. {v.toFixed(2)}
      </span>
    ),
  },
];

const InfoCard: React.FC<InfoCardProps> = ({ icon, label, value }) => (
  <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5 flex flex-col gap-1">
    <span className="text-[11px] text-gray-400 flex items-center gap-1.5">
      {icon}
      {label}
    </span>
    <span
      className="text-sm font-semibold text-gray-800 truncate"
      title={value}
    >
      {value}
    </span>
  </div>
);

const TotalRow: React.FC<TotalRowProps> = ({
  label,
  value,
  valueClass,
  bold,
}) => (
  <div className="flex items-center justify-between">
    <span
      className={`text-xs ${bold ? "font-semibold text-gray-800" : "text-gray-500"}`}
    >
      {label}
    </span>
    <span
      className={`text-xs ${bold ? "font-bold text-gray-900" : ""} ${valueClass ?? "text-gray-700"}`}
    >
      {value}
    </span>
  </div>
);

export const PurchaseInvoiceView: React.FC<Props> = ({ purchase }) => {
  const paymentStatus = purchase.payment_status as PurchasePaymentStatus;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg px-4 py-3">
        <CheckCircleFilled className="text-green-500 text-base" />
        <span className="text-sm font-semibold text-green-700">
          Purchase Completed &amp; Stock Updated
        </span>
        <Tag
          color={PAYMENT_TAG_COLOR[paymentStatus] ?? "default"}
          className="ml-auto text-xs font-semibold"
        >
          {paymentStatus}
        </Tag>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <InfoCard
          icon={<UserOutlined />}
          label="Supplier"
          value={purchase.supplier_name || "—"}
        />
        <InfoCard
          icon={<CalendarOutlined />}
          label="Purchase Date"
          value={dayjs(purchase.purchase_date).format("DD MMM YYYY")}
        />
        <InfoCard
          icon={<FileTextOutlined />}
          label="Invoice No"
          value={purchase.invoice_no || "—"}
        />
        <InfoCard
          icon={<FileTextOutlined />}
          label="PO No"
          value={purchase.po_no || "—"}
        />
      </div>

      <div>
        <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-2 px-1">
          Purchased Items
        </div>
        <Table
          dataSource={purchase.items || []}
          columns={itemColumns}
          pagination={false}
          size="small"
          rowKey={(rec, idx) => rec.id || `item-${idx}`}
          className="rounded-lg border border-gray-100 overflow-hidden"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No items found."
              />
            ),
          }}
        />
      </div>

      <div className="ml-auto w-full max-w-xs">
        <div className="bg-gray-50 border border-gray-100 rounded-lg p-4 flex flex-col gap-2.5">
          <TotalRow
            label="Gross Total"
            value={`Rs. ${purchase.total_amount.toFixed(2)}`}
          />
          <TotalRow
            label="Discount"
            value={`- Rs. ${purchase.discount_amount.toFixed(2)}`}
            valueClass="text-red-500"
          />
          <div className="border-t border-gray-200 pt-2">
            <TotalRow
              label="Grand Total"
              value={`Rs. ${purchase.grand_total.toFixed(2)}`}
              bold
            />
          </div>
          <TotalRow
            label="Paid"
            value={`Rs. ${purchase.paid_amount.toFixed(2)}`}
            valueClass="text-green-600"
          />
          <div className="border-t border-gray-200 pt-2 flex items-center justify-between">
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">
              Due Balance
            </span>
            <span
              className="text-lg font-bold"
              style={{
                color: PAYMENT_STATUS_COLOR[paymentStatus] ?? "#374151",
              }}
            >
              Rs. {purchase.due_amount.toFixed(2)}
            </span>
          </div>
          {purchase.due_date && purchase.due_amount > 0 && (
            <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
              <span>Due Date</span>
              <span className="font-medium text-gray-700">
                {dayjs(purchase.due_date).format("DD MMM YYYY")}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
